import { useQuery } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { GetTermsResponseDto, TermType } from '@/types/dtos/events/term.dto.ts';

export const GET_TERMS_QUERY_KEY = 'get-terms';
export const GET_PUBLISHED_VERSIONS_QUERY_KEY = 'get-published-versions';

export const useGetTermsQuery = (
  eventId: string | undefined,
  options?: {
    mode?: 'PLAN' | 'ORGANIZE';
    termType?: TermType;
    enabled?: boolean;
  },
) => {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: [GET_TERMS_QUERY_KEY, eventId, options?.mode, options?.termType],
    queryFn: async () => {
      if (!eventId) return null;
      const token = await getToken();
      return getTerms({ eventId, token: token ?? '', ...options });
    },
    enabled: (options?.enabled ?? true) && !!eventId,
  });
};

export const useGetPublishedVersionsQuery = (
  eventId: string | undefined,
  termType: TermType | undefined,
  options?: { enabled?: boolean },
) => {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: [GET_PUBLISHED_VERSIONS_QUERY_KEY, eventId, termType],
    queryFn: async () => {
      if (!eventId || !termType) return null;
      const token = await getToken();
      return getPublishedVersions({ eventId, termType, token: token ?? '' });
    },
    enabled: (options?.enabled ?? true) && !!eventId && !!termType,
  });
};

const getTerms = async ({
  eventId,
  token,
  mode,
  termType,
}: {
  eventId: string;
  token: string;
  mode?: 'PLAN' | 'ORGANIZE';
  termType?: TermType;
}): Promise<GetTermsResponseDto> => {
  const params = new URLSearchParams();
  if (mode) params.append('mode', mode);
  if (termType) params.append('termType', termType);

  const url = `${import.meta.env.VITE_API_URL}/events/${eventId}/terms${params.toString() ? `?${params.toString()}` : ''}`;
  return sendJSONRequest({
    url,
    method: METHOD.GET,
    token,
  });
};

const getPublishedVersions = async ({
  eventId,
  termType,
  token,
}: {
  eventId: string;
  termType: TermType;
  token: string;
}): Promise<GetTermsResponseDto> => {
  const url = `${import.meta.env.VITE_API_URL}/events/${eventId}/terms/${termType}/published`;
  return sendJSONRequest({
    url,
    method: METHOD.GET,
    token,
  });
};
